import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, firstValueFrom, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StatusList } from '../model/status-list.model';
import { Data } from '../model/data.model';
import { Beat, City, State } from '../model/address.model';
import { DdlModel, OrderBy, ProductList, UnitList } from '../model/ddl.model';
import { List } from '../model/list.model';
import { UserDDL, UserDocument } from '../model/b2b/orders/userConsole/super-distributor.model';
import { ProductType } from '../model/product.model';
import { SchemeDdl } from '../model/b2b/orders/scheme.model';
import { StorageService } from './storage.service';
import { ADMIN_TOKEN, routingPermission } from '../constants/constant';
import { ToastrService } from 'ngx-toastr';
import { StateService } from './state.service';
import { PrivilegesMenu } from '../model/adminPrivilegesList.model';
import { Router } from '@angular/router';
import { AdminService } from './admin.service';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  constructor(private http: HttpClient, private localStorage: StorageService,
    private toastrService: ToastrService, private stateService: StateService,
    private router: Router, private adminService: AdminService) { }

  getOrderStatusList(): Observable<Data<StatusList[]>> {
    return this.http.get<Data<StatusList[]>>(`${environment.apiURL}/getOrderstatusList`).pipe(
      catchError(this.handleError)
    )
  }

  getDashBoardOrderstatusList(): Observable<Data<StatusList[]>> {
    return this.http.get<Data<StatusList[]>>(`${environment.apiURL}/getDashBoardOrderstatusList`)
  }

  getAdminStateList(): Observable<Data<State[]>> {
    return this.http.get<Data<State[]>>(`${environment.apiURL}/Admin/getStateList`).pipe(
      catchError(this.handleError)
    )
  }

  getCityListByStateIds(obj: { stateList: number[], searchString: '' }): Observable<Data<State[]>> {
    return this.http.post<Data<State[]>>(`${environment.apiURL}/getCityListByStateList`, obj)
  }

  getCityList(obj: any): Observable<Data<City[]>> {
    return this.http.post<Data<City[]>>(`${environment.apiURL}/getCityList`, obj).pipe(
      catchError(this.handleError)
    )
  }

  getZoneDDL(): Observable<Data<any>> {
    return this.http.get<Data<any>>(`${environment.apiURL}/api/v1/B2B/Admin/GetZoneListddl`).pipe(
      catchError(this.handleError)
    )
  }

  getCityListSearchByName(obj: any): Observable<Data<City[]>> {
    return this.http.post<Data<City[]>>(`${environment.apiURL}/getCityListSearchByName`, obj);
  }

  getBeatList(obj: any): Observable<Data<Beat[]>> {
    return this.http.post<Data<Beat[]>>(`${environment.apiURL}/getBeatListWeb`, obj).pipe(
      catchError(this.handleError)
    )
  }

  getStdUnitDdl(): Observable<Data<UnitList[]>> {
    return this.http.get<Data<UnitList[]>>(`${environment.apiURL}/Admin/GetStdUnitDdl `);
  }

  getB2CUnitNameDDL(): Observable<Data<UnitList[]>> {
    return this.http.get<Data<UnitList[]>>(`${environment.apiURL}/GetB2CUnitNameDDL`);
  }

  adminOrderByDDL(): Observable<Data<OrderBy[]>> {
    return this.http.get<Data<OrderBy[]>>(`${environment.apiURL}/api/v1/B2B/Admin/OrderByDDL`);
  }

  requestedByDDL(): Observable<Data<OrderBy[]>> {
    return this.http.get<Data<OrderBy[]>>(`${environment.apiURL}/api/v1/B2B/RequestedByDDL`);
  }

  superDistributorDDL(): Observable<any> {
    return this.http.get<any>(`${environment.apiURL}/api/v1/B2B/SuperDistributorDDL`, { headers: this.setTokenToHeader() });
  }

  distributorDDL(): Observable<any> {
    return this.http.get<any>(`${environment.apiURL}/api/v1/B2B/DistributorDDL`, { headers: this.setTokenToHeader() });
  }

  getSlabDDL(): Observable<Data<DdlModel[]>> {
    return this.http.get<Data<DdlModel[]>>(`${environment.apiURL}/api/v1/B2B/Admin/GetSlabList`);
  }

  distributorBySPIdDDL(userId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/api/v1/B2B/admin/DistributorBySPIdDDL`, { "userId": userId });
  }

  retailerBySPIdDDL(userId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/api/v1/B2B/admin/RetailerBySPIdDDL`, { "userId": userId });
  }

  gangotriUsersByRoleIdDDL(roleId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/api/v1/B2B/admin/GangotriUsersByRoleIdDDL`, { "roleId": roleId });
  }

  getRoleList(): Observable<any> {
    return this.http.get<any>(`${environment.apiURL}/api/v1/B2B/GetUserRoleList`);
  }

  approvalStatusDDL(): Observable<any> {
    return this.http.get<any>(`${environment.apiURL}/api/v1/B2B/ApprovalStatusDDL`);
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    let errorMessage = '';
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    }
    else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;

    }
    return throwError(() => errorMessage);
  }

  getDocumentList(): Observable<Data<UserDocument>> {
    return this.http.get<Data<UserDocument>>(`${environment.apiURL}/api/v1/B2B/admin/DocTypeDDL`);
  }

  downloadCatalogue(): Observable<Data<any>> {
    return this.http.get<Data<any>>(`${environment.apiURL}/api/v1/B2B/admin/Download/Catalogue`);
  }

  getCategory(): Observable<Data<List<ProductType>>> {
    return this.http.post<Data<List<ProductType>>>(
      `${environment.apiURL}/AdminCategory/GetCategory`, {
      "pageNo": 1,
      "pageSize": 100
    }
    );
  }

  getIssueTypeList(): Observable<Data<ProductType[]>> {
    return this.http.get<Data<ProductType[]>>(`${environment.apiURL}/api/v1/B2B/GetIssueCategoryDDL`);
  }

  getSchemeDdl(): Observable<Data<SchemeDdl>> {
    return this.http.get<Data<SchemeDdl>>(`${environment.apiURL}/api/v1/B2B/admin/Scheme/getSchemeDdl`);
  }

  getSubCategoryByCatIdDdl(categoryIdArr: number[]): Observable<Data<ProductType[]>> {
    return this.http.post<Data<ProductType[]>>(`${environment.apiURL}/api/v1/B2B/admin/Scheme/GetSubCategoryByCatIdDdl`, categoryIdArr);
  }

  getDistributorDdlByUserIdArr(superDistributorIdArr: number[]): Observable<Data<UserDDL[]>> {
    return this.http.post<Data<UserDDL[]>>(`${environment.apiURL}/api/v1/B2B/admin/Scheme/DistributorDdlByUserIdArr`, superDistributorIdArr);
  }

  getProductListDdl(productListDdlObj: { searchString: string, subCatId: number[] }): Observable<Data<ProductList[]>> {
    return this.http.post<Data<ProductList[]>>(`${environment.apiURL}/api/v1/B2B/admin/Scheme/GetProductListDdl`, productListDdlObj);
  }

  getUserParentDetailsDDL(): Observable<Data<ProductList[]>> {
    return this.http.get<Data<ProductList[]>>(`${environment.apiURL}/api/v1/B2B/admin/UserParentDetailsDDL`);
  }

  getGstSlabDdl(): Observable<Data<ProductList[]>> {
    return this.http.get<Data<ProductList[]>>(`${environment.apiURL}/Admin/GetGstSlabDdl`);
  }

  getNoSaleCatDdl(): Observable<any> {
    return this.http.get<any>(`${environment.apiURL}/api/v1/B2B/admin/NoSaleCatDdl`);
  }

  companyTypeDdl(): Observable<any> {
    return this.http.get(`${environment.apiURL}/AdminUser/companyTypeDdl`)
  }

  cityDdlGoogle(searchString: string): Observable<any> {
    return this.http.post(`${environment.apiURL}/AdminUser/cityDdlGoogle`, { searchString: searchString })
  }

  setTokenToHeader() {
    const headers: any = {};
    let jwt = this.localStorage.getItem(ADMIN_TOKEN);
    if (jwt) {
      headers['authorization'] = `Bearer ${jwt}`;
    }
    return headers
  }

  async handleSuccessfulLogin(res: any) {
    localStorage.clear()
    localStorage.setItem(ADMIN_TOKEN, res.token);

    let privilegesMenu = await firstValueFrom(this.stateService.privilegesMenu$);
    if (!privilegesMenu.length) {
      privilegesMenu = (await firstValueFrom(this.adminService.getMenuActions())).data;
      this.stateService.sendPrivilegesMenu(privilegesMenu);
    }

    let companyPermission = await firstValueFrom(this.stateService.companyPermision$);
    if (!companyPermission.length) {
      const companyPermissionList = await firstValueFrom(this.adminService.getCompanyPermission());
      companyPermission = companyPermissionList.data;
      this.stateService.sendCompanyPermision(companyPermission);
    }

    this.toastrService.success(res.meta.status_message);
    this.stateService.shimmerLoaderUpdate(false);

    if (privilegesMenu.findIndex((menuAction: PrivilegesMenu) => menuAction.menuId === routingPermission.dashboard && menuAction.view) !== -1) {
      this.router.navigate(['/admin/b2b/dashboard']);
    } else if (privilegesMenu.findIndex((menuAction: PrivilegesMenu) => menuAction.menuId === 26 && menuAction.view) !== -1) {
      this.router.navigate(['/admin/b2b/sales/sales-dashboard']);
    } else {
      this.router.navigate(['/admin/b2b/profile']);
    }
  }
}
